import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, Mail, MessageSquare, ArrowLeft, Lock, KeyRound } from "lucide-react";
import { api, errorText } from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { Logo } from "@/components/Logo";
import { inputCls, Label, Button } from "@/admin/ui";

const METHODS = [
  { id: "totp", label: "Authenticator app", icon: Smartphone, desc: "Google Authenticator / Authy code" },
  { id: "email", label: "Email code", icon: Mail, desc: "6-digit code sent to your admin email" },
  { id: "sms", label: "SMS code", icon: MessageSquare, desc: "6-digit code sent to your phone" },
];

export default function Login() {
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [pre, setPre] = useState(null);
  const [method, setMethod] = useState("totp");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const auth = { headers: { Authorization: `Bearer ${pre?.pre_token}` } };

  const submitCreds = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", creds);
      setPre(data);
      setStep(2);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async (m) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/otp/send", { method: m }, auth);
      setSentTo(data.to);
      toast.success(`Code sent to ${data.to}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const pickMethod = async (m) => {
    setMethod(m);
    setCode("");
    if (m !== "totp") await sendCode(m);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify", { method, code }, auth);
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name.split(" ").slice(-1)[0]}.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const setup = pre?.totp_setup;

  return (
    <div className="min-h-screen bg-[#0B1528] flex items-center justify-center p-4 relative overflow-hidden" data-testid="admin-login-page">
      <div className="hero-grid-dark absolute inset-0" />
      <div className="glow-amber-light absolute -top-40 -right-20 h-[600px] w-[600px] rounded-full" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="relative w-full max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <Logo dark />
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">
            <Lock className="h-3 w-3 text-amber-400" /> Admin
          </span>
        </div>

        <div className="rounded-3xl bg-white p-8 shadow-2xl">
          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.form key="s1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={submitCreds} className="space-y-5" data-testid="admin-login-form">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-amber-600 mb-2">Step 1 of 2</p>
                  <h1 className="text-2xl font-bold text-[#0B1528]">Sign in to Desantrix Admin</h1>
                  <p className="mt-1 text-sm text-slate-500">Restricted area. Every attempt is logged.</p>
                </div>
                <div>
                  <Label htmlFor="admin-username">Username</Label>
                  <input id="admin-username" data-testid="admin-username-input" autoComplete="username" required value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="admin-password">Password</Label>
                  <input id="admin-password" data-testid="admin-password-input" type="password" autoComplete="current-password" required value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className={inputCls} />
                </div>
                {error && <p className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
                <Button type="submit" loading={busy} className="w-full py-3.5" data-testid="admin-login-submit">
                  <KeyRound className="h-4 w-4" /> Continue
                </Button>
              </motion.form>
            ) : (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="admin-2fa-step">
                <button onClick={() => { setStep(1); setError(""); }} className="mb-4 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0B1528]" data-testid="admin-2fa-back">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-amber-600 mb-2">Step 2 of 2 · Verification</p>
                <h2 className="text-xl font-bold text-[#0B1528] mb-5 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-500" /> Confirm it's you</h2>

                <div className="grid grid-cols-3 gap-2 mb-5" data-testid="admin-2fa-methods">
                  {METHODS.map((m) => {
                    const enabled = pre?.methods?.[m.id];
                    return (
                      <button
                        key={m.id}
                        type="button"
                        disabled={!enabled || busy}
                        onClick={() => pickMethod(m.id)}
                        data-testid={`admin-2fa-method-${m.id}`}
                        title={enabled ? m.desc : "Not configured yet"}
                        className={`rounded-xl border p-3 text-left transition-all ${method === m.id ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"} disabled:opacity-40 disabled:cursor-not-allowed`}
                      >
                        <m.icon className={`h-4 w-4 mb-1.5 ${method === m.id ? "text-amber-600" : "text-slate-500"}`} />
                        <span className="block text-xs font-semibold text-[#0B1528] leading-tight">{m.label}</span>
                      </button>
                    );
                  })}
                </div>

                {method === "totp" && setup && (
                  <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="admin-totp-setup">
                    <p className="text-sm font-semibold text-[#0B1528] mb-1">First-time setup</p>
                    <p className="text-xs text-slate-600 mb-3">Scan this QR with Google Authenticator or Authy, then enter the 6-digit code below.</p>
                    <div className="flex items-center gap-4">
                      <img src={setup.qr} alt="Authenticator QR code" className="h-32 w-32 rounded-lg border border-slate-200 bg-white" data-testid="admin-totp-qr" />
                      <div className="min-w-0">
                        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">Manual key</p>
                        <code className="block break-all text-xs text-[#0B1528]" data-testid="admin-totp-secret">{setup.secret}</code>
                      </div>
                    </div>
                  </div>
                )}
                {method !== "totp" && sentTo && <p className="mb-4 text-xs text-slate-500">Code sent to <span className="font-semibold text-[#0B1528]">{sentTo}</span>. <button type="button" onClick={() => sendCode(method)} className="text-amber-600 hover:underline" data-testid="admin-otp-resend">Resend</button></p>}

                <form onSubmit={verify} className="space-y-4" data-testid="admin-2fa-form">
                  <div>
                    <Label htmlFor="admin-code">6-digit code</Label>
                    <input id="admin-code" data-testid="admin-code-input" inputMode="numeric" autoComplete="one-time-code" required minLength={6} maxLength={8} value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))} placeholder="••••••" className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono`} />
                  </div>
                  {error && <p className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-600" data-testid="admin-2fa-error">{error}</p>}
                  <Button type="submit" loading={busy} className="w-full py-3.5" data-testid="admin-2fa-submit">
                    <ShieldCheck className="h-4 w-4" /> Verify & sign in
                  </Button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <p className="mt-6 text-center text-[11px] text-slate-500">Protected by password + two-factor verification, brute-force lockout and rate limiting.</p>
      </motion.div>
    </div>
  );
}
